/**
 * Shopping cart functionality for the Shopify theme
 * Handles cart items management, shipping calculations, points system, and dynamic pricing
 * Includes tiered pricing logic, country-specific shipping fees, and cart updates
 */

import * as currency from "@shopify/theme-currency";

/**
 * Creates fetch configuration object for API requests
 * @param {string} type - Response type (json, javascript, etc.)
 * @returns {Object} Fetch configuration object
 */
function fetchConfig(type = "json") {
  return {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: `application/${type}`,
    },
  };
}

class CartPoints extends HTMLElement {
  constructor() {
    super();
    this.pointsElement = this.querySelector('[data-cart-points]');
  }
  
  /**
   * Updates the cart points dynamically based on the cart data.
   * @param {Object} cart - The cart object from Shopify's API.
   */
  update(cartTotalPrice = 0) {
    if (!this.pointsElement) return;
    // Determine the tier multiplier (tier_math)
    let tierMath = 5; // Default multiplier
    if (window.customerTier) {
      if (window.customerTier === 'Advanced') tierMath = 6;
      if (window.customerTier === 'Pro') tierMath = 7;
    }

    // Calculate points
    const points = Math.ceil((cartTotalPrice / 100) * tierMath);

    // Update the points display
    this.pointsElement.dataset.points = points;
    this.pointsElement.innerHTML = this.pointsElement.innerHTML.replace(/\d+/, points);
  }
}

if (!customElements.get('cart-points')) {
  customElements.define('cart-points', CartPoints);
}

class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.cartDrawerItem = this.closest(".cart-drawer-item");
    this.addEventListener("click", (event) => {
      event.preventDefault();
      this.closest("cart-items").updateQuantity(this.dataset.index, 0);
    });
  }
}

if (!customElements.get("cart-remove-button")) {
  customElements.define("cart-remove-button", CartRemoveButton);
}

class CartItems extends HTMLElement {
  constructor() {
    super();

    // Clone object with theme settings from snippets/html-bottom-js.liquid
    this.config = JSON.parse(JSON.stringify(window.cartShippingFee));

    // Find lineItemStatusElement
    this.lineItemStatusElement = document.getElementById("shopping-cart-line-item-status");

    this.currentItemCount = Array.from(
      this.querySelectorAll('[name="updates[]"]')
    ).reduce((total, quantityInput) => total + parseInt(quantityInput.value),0);

    this.debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, 300);

    this.addEventListener("change", this.debouncedOnChange.bind(this));

    this.cartPoints = document.querySelector("cart-points");
    // Check visitor country
    this.getVisitorCountry().then((country) => {
      const countryCode = country.toLowerCase();
      this.config.countryCode = countryCode;
      this.config.region = 'restOfWorld';
      if (this.config.nearbyEUCountries.countries.includes(countryCode)) {
        this.config.region = 'nearbyEUCountries';
      }
      if (this.config.otherEUCountries.countries.includes(countryCode)) {
        this.config.region = 'otherEUCountries';
      }
      this.productRecommendationsInline = document.querySelector('product-recommendations-inline');
      if (this.productRecommendationsInline) {
        this.productRecommendationsInline.getRecommendations();
      }
      this.calculateShippingFee();
    });
  }

  /**
   * Fetches visitor's country from Shopify's browsing context API
   * @returns {Promise<string>} Country code
   */
  async getVisitorCountry() {
    const response = await fetch("/browsing_context_suggestions.json");
    const data = await response.json();
    let country = data.detected_values.country.handle;
    return country;
  }

  /**
   * Calculates shipping fees based on country, cart total, and shipping thresholds
   * Updates UI with shipping costs and total price
   */
  calculateShippingFee() {



    // Get current subtotal price from cart
    let subtotalPrice = parseInt(document.querySelector("[data-subtotal]").dataset.subtotal);
    let shippingFee = this.config[this.config.region].shippingFee;
    let reducedShippingFee = this.config[this.config.region].reducedShippingFee;
    let reducedShippingThreshold = this.config[this.config.region].reducedShippingThreshold;
    let freeShippingThreshold = this.config[this.config.region].freeShippingThreshold;
    let shippingFeeElement = document.querySelector("[data-cart-shipping-costs]");
    let shippingStickyElement = document.querySelector("[data-sticky-free-shipping]");
    let totalPriceElement = document.querySelector("[data-cart-total-price]");
    let totalPriceStickyElement = document.querySelector("[data-sticky-total-price]");

    // Check if Reduced shipping qualifies
    if (reducedShippingThreshold && subtotalPrice >= reducedShippingThreshold) {
      
      shippingFee = reducedShippingFee;
    }
    // Check if Free shipping qualifies
    if (freeShippingThreshold && subtotalPrice >= freeShippingThreshold) {
      shippingFee = 0;
    }

    // Sum shipping costs and new subtotal price
    const newTotalPrice = shippingFee + subtotalPrice;

    if (shippingFee == 0 ) {
      // If shipping costs are 0, render Free label
      shippingFeeElement.innerHTML = this.config.freeLabel.replace(/['"]+/g, "");
      if (shippingStickyElement) {
        shippingStickyElement.classList.remove('hidden');
      }
    } else {
        if (shippingStickyElement) {
            shippingStickyElement.classList.add('hidden');
        }
      // Else show shipping costs in money
      shippingFeeElement.innerHTML = currency.formatMoney(shippingFee, this.config.shopCurrency).replace(/['"]+/g, "");
    }

    // Render new total price
    if (totalPriceElement) {
      totalPriceElement.innerHTML = currency.formatMoney(newTotalPrice, this.config.shopCurrency).replace(/['"]+/g, "");
    }
    
    // Update total price
    if (totalPriceStickyElement) {
      totalPriceStickyElement.innerHTML = currency.formatMoney(newTotalPrice, this.config.shopCurrency).replace(/['"]+/g, "");
    }

    // Update cart points
    if (this.cartPoints) {
      this.cartPoints.update(newTotalPrice);
    }
  }

  /**
   * Handles quantity input changes in cart
   * @param {Object} param - Event object with target
   * @param {HTMLElement} param.target - The input element that changed
   */
  onChange({ target }) {
    this.updateQuantity(
      target.dataset.index,
      target.valueAsNumber,
      document.activeElement.getAttribute("name")
    );
    if (this.productRecommendationsInline) {
      this.productRecommendationsInline.getRecommendations();
    }

  }

  /**
   * Returns array of sections to render when cart updates
   * @returns {Array} Array of section configuration objects
   */
  getSectionsToRender() {
    return [
      {
        id: "main-cart-items",
        section: document.getElementById("main-cart-items").dataset.id,
        selector: ".js-cart-item-contents",
      },
      {
        id: "main-cart-footer",
        section: document.getElementById("main-cart-footer").dataset.id,
        selector: ".js-cart-footer-contents",
      },
      {
        id: "cart-icon-bubble",
        section: "cart-icon-bubble",
        selector: "[data-cart-icon-bubble]",
      },      
      {
        id: 'product-recommendations-inline',
        section: 'product-recommendations-inline',
        selector: `[data-recommended-products-inline]`,
      },
    ];
  }

  /**
   * Returns array of sections to render when quantity changes
   * @returns {Array} Array of section configuration objects
   */
  getSectionsToRenderOnChange() {
    return [
      {
        id: "main-cart-items",
        section: document.getElementById("main-cart-items").dataset.id,
        selector: ".js-cart-item-contents",
      },
      {
        id: "main-cart-footer",
        section: document.getElementById("main-cart-footer").dataset.id,
        selector: ".js-cart-footer-contents",
      },
      {
        id: "cart-icon-bubble",
        section: "cart-icon-bubble",
        selector: "[data-cart-icon-bubble]",
      },
    ];
  }

  /**
   * Updates cart item quantity via AJAX and refreshes cart sections
   * @param {number} line - Cart line item index
   * @param {number} quantity - New quantity
   * @param {string} name - Name of the input field that triggered the update
   */
  async updateQuantity(line, quantity, name) {
    this.enableLoading();

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRenderOnChange().map((section) => section.section),
      sections_url: window.location.pathname,
    });

    try {
      const response = await fetch(`${routes.cart_change_url}`, {
        ...fetchConfig(),
        ...{ body },
      });
      const { sections, item_count, items } = await response.json();

      // Toggle classes
      const classToToggle = sections["cart-drawer"]
        ? "cart-drawer-is-empty"
        : "is-empty";
      this.classList.toggle(classToToggle, item_count === 0);
      document
        .getElementById("main-cart-footer")
        ?.classList.toggle("is-empty", item_count === 0);

      // Render the sections
      this.getSectionsToRenderOnChange().forEach((section) => {
        const { id, selector, section: newSection } = section;
        const elementToReplace =
          document.getElementById(id).querySelector(selector) ||
          document.getElementById(id);
        elementToReplace.innerHTML = this.getSectionInnerHTML(
          sections[newSection],
          selector
        );
      });

      if (name == 'remove') {
        this.updateLiveRegions(line, item_count);
      }
      document
        .getElementById(`CartItem-${line}`)
        ?.querySelector(`[name="${name}"]`)
        ?.focus();

      // Calculate shipping costs and total price and show on Front End after cart change
      this.calculateShippingFee();

      // Get the item_count multiplied by the item count metafield (for bundled products)
      let itemCountWithBundles = 0;
      items.forEach(
        (item) =>
          (itemCountWithBundles +=
            item.quantity * item.properties._tiered_pricing_count)
      );
      this.handleAllPricesAndBadges(itemCountWithBundles);
    } catch (error) {
      console.error(error);
      document.querySelector(".loading-overlay").classList.add("hidden");
      document.getElementById("cart-errors").textContent = window.cartStrings.error;
    } finally {
      this.disableLoading();
    }
  }

  // show error texts, should not be deleted.
  /**
   * Updates accessibility live regions for screen readers
   * @param {number} line - Cart line item index
   * @param {number} itemCount - Current item count
   */
  updateLiveRegions(line, itemCount) {
    if (this.currentItemCount === itemCount && document.getElementById(`Quantity-${line}`) ) {
      let lineItemErrorLine = document.getElementById(`Line-item-error-${line}`);
      let lineItemErrorText = lineItemErrorLine.querySelector(".cart-item__error-text") || lineItemErrorLine.querySelector(".cart-drawer-item__error-text");
      lineItemErrorText.innerHTML = window.cartStrings.quantityError.replace(
          "[quantity]",
          document.getElementById(`Quantity-${line}`).value
        );
    }

    this.currentItemCount = itemCount;
    this.lineItemStatusElement.setAttribute("aria-hidden", true);

    const cartStatus = document.getElementById("cart-live-region-text");
    cartStatus.setAttribute("aria-hidden", false);

    setTimeout(() => {
      cartStatus.setAttribute("aria-hidden", true);
    }, 1000);
  }

  /**
   * Extracts inner HTML from a section response
   * @param {string} html - HTML response string
   * @param {string} selector - CSS selector to extract
   * @returns {string} Inner HTML of the selected element
   */
  getSectionInnerHTML(html, selector) {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector).innerHTML;
  }

  /**
   * Shows loading state on cart interface
   */
  enableLoading() {
    document
      .getElementById("main-cart-items")
      .classList.add("cart__items--disabled");
    document.querySelector(".loading-overlay").classList.remove("hidden");
    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute("aria-hidden", false);
  }

  /**
   * Hides loading state on cart interface
   */
  disableLoading() {
    document
      .getElementById("main-cart-items")
      .classList.remove("cart__items--disabled");
    document.querySelector(".loading-overlay").classList.add("hidden");
  }

  /**
   * Updates all tiered pricing elements based on cart item count
   * @param {number} item_count - Total count of items (including bundles)
   */
  handleAllPricesAndBadges(item_count) {
    const tiers = window.tieredPricing.tiers;
    const pricesElements = document.querySelectorAll("tiered-pricing-price");
    const badgesElements = document.querySelectorAll("tiered-pricing-badge");

    // Update the window value
    window.tieredPricing.tiered_pricing_item_count = item_count + 1;

    tiers.forEach(({ tiered_pricing_threshold, tiered_pricing_discount }) => {
      pricesElements.forEach((element) =>
        element.handlePrice(
          false,
          tiered_pricing_threshold,
          tiered_pricing_discount
        )
      );
      badgesElements.forEach((element) =>
        element.handleBadge(
          false,
          tiered_pricing_threshold,
          tiered_pricing_discount
        )
      );
    });
  }
}

if (!customElements.get("cart-items")) {
  customElements.define("cart-items", CartItems);
}
